import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import styles from './PropertyCardSearch.scss';
import PropTypes from 'prop-types';
import moment from 'moment';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import propertyWriter from '../../lib/urlWriters/webURL';
import {propertyPriceLabel} from '../../lib/formatting';
import PropertyAttribute from '../PropertyAttribute/PropertyAttribute';
import FavouriteStar from '../Favourites/FavouriteStar';
import PropertyCategoryRibbon from '../PropertyCategoryRibbon/PropertyCategoryRibbon';
import Gauge from '../Liveability/Gauge';
import CardCarousel from '../Carousel/CardCarousel';
import {propertyTypeGroups} from '../../lib/config';
import {trackSelectItem} from '../../lib/tracking';
import {trackLiveability} from '../Liveability/tracking';
import PropertyCardFooter from './PropertyCardFooter';
import PackageRibbon from '../PropertyRibbon/PackageRibbon';

const apartmentPropertyTypes = propertyTypeGroups.find(ptg => ptg.name === 'Apartment').types.map(propertyType => propertyType);
const townhousePropertyTypes = propertyTypeGroups.find(ptg => ptg.name === 'Townhouse').types.map(propertyType => propertyType);
const commercialPropertyTypes = propertyTypeGroups.find(ptg => ptg.name === 'Commercial').types.map(propertyType => propertyType);

const liveabilityThreshold = 8;

class PropertyCardSearch extends Component {
  constructor(props) {
    super(props);
    this.isTouchDevice = (typeof window === 'object' && 'ontouchstart' in window) || (typeof navigator === 'object' && !!navigator.msMaxTouchPoints);
  }

  trackClick = () => trackSelectItem(this.props.property);

  trackLiveabilityClick = ({type}) => {
    if (!this.isTouchDevice || type === 'touchstart') {
      trackLiveability('liveability_click', this.props.property);
    }
  };

  trackLiveabilityHover = () => {
    if (!this.isTouchDevice) {
      trackLiveability('liveability_gauge_hover', this.props.property);
    }
  };

  getListedDateText = () => {
    const {property: {listing = {}} = {}} = this.props;
    let listedDateText;

    const currentDateTime = moment();
    const listedDateTime = moment(listing.listedDateTime);

    if (!listing.listedDateTime || currentDateTime.diff(listedDateTime, 'days') > 5) {
      listedDateText = null;
    } else if (currentDateTime.diff(listedDateTime, 'hours') >= 24) {
      const daysDiff = currentDateTime.diff(listedDateTime, 'days');
      listedDateText = `Added ${daysDiff} day${daysDiff > 1 ? 's' : ''} ago`;
    } else if (currentDateTime.diff(listedDateTime, 'minutes') >= 60) {
      const hoursDiff = currentDateTime.diff(listedDateTime, 'hours');
      listedDateText = `Added ${currentDateTime.diff(listedDateTime, 'hours')} hour${hoursDiff > 1 ? 's' : ''} ago`;
    } else {
      listedDateText = 'Added now';
    }

    return listedDateText;
  };

  render() {
    let property = this.props.property;
    let isListing = !!property.category.match(/SaleListing|RentalListing/);
    let isMembers = this.props.type === 'members';
    let hasTour = isListing && !!property.listing.hasVirtualTour;
    let hasLiveability = property.liveability && property.liveability.livebilityIndex >= liveabilityThreshold;

    const listing = property.listing ? property.listing : {};
    const nextInspectionTimes = _isArray(listing.inspectionTimes) ?
      listing.inspectionTimes.filter(it => moment() < moment(it.end)) : [];
    const nextInspectionTime = nextInspectionTimes.length > 0 && nextInspectionTimes[0].start ? moment(nextInspectionTimes[0].start).format('D MMM h:mm a') : null;

    const isApartment = apartmentPropertyTypes.some(type => type.toLowerCase() === property.type.toLowerCase());
    const isTownhouse = townhousePropertyTypes.some(type => type.toLowerCase() === property.type.toLowerCase());
    const isCommercial = commercialPropertyTypes.some(type => type.toLowerCase() === property.type.toLowerCase());

    const listedDateText = this.getListedDateText();
    const {price, ribbonText} = propertyPriceLabel(property, {});
    const propertyUrl = propertyWriter(property);

    const {formattedAddress, suburb} = property.address;

    let addressLine1 = formattedAddress;
    let addressLine2 = '';
    if (!_isEmpty(suburb)) {
      addressLine1 = formattedAddress.substr(0, formattedAddress.toLowerCase().lastIndexOf(suburb.toLowerCase()));
      addressLine2 = property.address.formattedAddress.substring(addressLine1.length);
    }

    return (
      <div className={'card ' + (this.props.mapCard ? styles.map : 'mb-3') + ' ' + styles.propertyCard + (this.props.type ? ' ' + styles[this.props.type] : '')}>
        <div className={styles.cardImageBlock}>

          {hasTour && !hasLiveability ? <div className={styles.virtualTourIcon}><Link to={propertyUrl + '?showTour=true'}><img title="Click to view the Virtual Tour" className="w-100" src="/assets/images/3d_square@2x.png" /></Link></div> : null}
          {hasLiveability ? <div className={styles.liveabilityGauge} onTouchStart={this.trackLiveabilityClick} onClick={this.trackLiveabilityClick} onMouseEnter={this.trackLiveabilityHover}><Gauge value={this.props.property.liveability.livebilityIndex} url={propertyUrl} display="srp" boxed={true} threshold={liveabilityThreshold} /></div> : null}
          <CardCarousel property={property} fetchImages={this.props.fetchImages} mapCard={this.props.mapCard} />
          {property.underOffer ?
            <Link to={propertyUrl} className={`${styles.ribbon} ${styles.underOffer}`}>Under Offer</Link>
            : (listedDateText) ?
              <Link to={propertyUrl} className={`${styles.ribbon} ${styles.listedDate}`}>{listedDateText}</Link>
              : (property.recentlySold) ?
                <Link to={propertyUrl} className={`${styles.ribbon} ${styles.recentlySold}`}>{ribbonText}</Link>
                : null}
        </div>
        <Link onClick={this.trackClick} to={propertyUrl}>
          <div className={'flex-grow-1 d-flex flex-column justify-content-between ' + styles.propertyCardBody}>
            <div className="flex-grow-1 d-flex flex-column">
              <div className={styles.propertyCategory}>
                <PropertyCategoryRibbon category={this.props.property.category} isRecentlySold={this.props.property.recentlySold} customStyle={{height: '24px', lineHeight: 1.3, fontWeight: 400, fontSize: '14px', letterSpacing: '0.1px' }}/>
                <PackageRibbon property={this.props.property} listing={listing} />
                {!isMembers ?
                  <div className={styles.propertyFavourite}>
                    <FavouriteStar
                      size="sm"
                      propertyId={this.props.property.propertyId}
                      property={this.props.property}
                      listingId={this.props.property.id}
                      useNewIcon={true}
                      isFromCollection={this.props.isFromCollection}
                    />
                  </div> :
                  null}
              </div>
              <div className={`text-truncate ${styles.propertyCardPrice}`}>
                {price !== '' ? <span className={styles[this.props.property.category]}>{price}</span> : ''}
              </div>
              <div className={'text-capitalize ' + styles.propertyCardText}>
                <div className="text-truncate"><h2 className={styles.propertyCardAddressText}>{addressLine1}</h2></div>
                <div className="text-truncate"><h2 className={styles.propertyCardAddressText}>{addressLine2}</h2></div>
              </div>
              <div className="d-flex justify-content-between">
                <ul className={'p-0 d-flex justify-content-start smText ' + styles.propertyCardAttributes}>
                  <li className="mr-2"><PropertyAttribute type="beds" value={property.beds}/></li>
                  <li className="mr-2"><PropertyAttribute type="baths" value={property.baths}/></li>
                  <li className="mr-2"><PropertyAttribute type="carSpaces" value={property.carSpaces}/></li>
                  { ((isApartment || isTownhouse || isCommercial) && property.floorSize) || (!(isApartment || isTownhouse || isCommercial) && property.landSize) ?
                    <li className="mr-2">
                      <PropertyAttribute gray type={(isApartment || isTownhouse || isCommercial) ? 'floorSize' : 'landSize'}
                        value={(isApartment || isTownhouse  || isCommercial) ? property.floorSize : property.landSize}
                        unit={(isApartment || isTownhouse || isCommercial) ? null : property.landSizeUnit} />
                    </li>
                    : null
                  }
                </ul>
                <div className={'smText text-right greyText'} ><span data="seoData">Type: </span>{this.props.property.type}</div>
              </div>
              <div className={'d-flex ' + styles.propertyCardNextInspection}>
                {nextInspectionTime ? <div className={`${styles.inspectionTime} ${this.props.property.category}`}>{`Next inspection ${nextInspectionTime}`}</div> : null}
              </div>
            </div>
          </div>
        </Link>
        <div className={'d-flex align-items-center ' + styles.thinLine + ' ' + styles.cardFoot}>
          <PropertyCardFooter property={this.props.property} propertyUrl={propertyUrl} isMembers={isMembers}
            isListing={isListing} isPropertyTracked={this.props.isPropertyTracked} />
        </div>
      </div>
    );
  }
}

PropertyCardSearch.propTypes = {
  property: PropTypes.object.isRequired,
  type: PropTypes.string,
  showAvatar: PropTypes.bool,
  mapCard: PropTypes.bool,
  isFromCollection: PropTypes.bool,
  fetchImages: PropTypes.bool,
};

export default PropertyCardSearch;
